import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, Heart, Target, Shield, Waves, MapPin } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const AboutUs = ({ user, onLogout }) => {
  return (
    <div className="min-h-screen">
      <Navigation user={user} onLogout={onLogout} />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-linear-to-br from-cyan-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
              About Streamline Aquatics
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Building confidence and champions in Ghana's waters since 2015
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                <p>
                  Streamline Aquatics Limited was founded with a simple mission: to make swimming accessible,
                  safe, and enjoyable for everyone in Ghana. What started as a small learn-to-swim program
                  for schools has grown into one of the nation's premier swimming lesson providers.
                </p>
                <p>
                  Over the years, we've taught thousands of Ghanaians to swim, developed competitive athletes
                  who've represented the country internationally, and partnered with schools across the region
                  to bring quality swimming education to their students.
                </p>
                <p>
                  Today, we're proud to operate aquatic facilities, employ certified instructors,
                  and maintain the highest safety standards in the industry.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden shadow-2xl"
            >
              <img
                src="https://images.unsplash.com/photo-1763930286426-6f17a720253b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMHN3aW1taW5nJTIwcG9vbCUyMHdhdGVyJTIwdGV4dHVyZSUyMGJyaWdodHxlbnwwfHx8fDE3Njc4MTk3NTV8MA&ixlib=rb-4.1.0&q=85"
                alt="Swimming pool facility"
                className="w-full h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'Safety First',
                description: 'Every decision we make prioritizes the safety and well-being of our swimmers. From certified lifeguards to rigorous safety protocols, we never compromise.',
              },
              {
                icon: Heart,
                title: 'Family-Centered',
                description: 'We create a warm, welcoming environment where families feel at home. Swimming is a family activity, and we treat every member like part of our Streamline family.',
              },
              {
                icon: Award,
                title: 'Excellence',
                description: 'We pursue excellence in instruction, facilities, and service. Our instructors undergo continuous training to deliver the best swimming education possible.',
              },
              {
                icon: Users,
                title: 'Community',
                description: 'We believe in building community through swimming. Our programs bring people together and create lasting friendships built on shared experiences.',
              },
              {
                icon: Target,
                title: 'Progress',
                description: 'Every swimmer progresses at their own pace. We celebrate achievements big and small, from first kicks to competitive medals.',
              },
              {
                icon: Waves,
                title: 'Innovation',
                description: 'We constantly evolve our programs and facilities to meet the changing needs of our community while maintaining the highest standards.',
              },
            ].map((value, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg"
                data-testid={`value-card-${i}`}
              >
                <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-cyan-700" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Facility</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Achimota Swimming & Leisure Center - A complete aquatics destination
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: '25-Yard Competition Pool',
                description: '5-lane pool maintained at optimal temperature with advanced filtration systems, perfect for training and competitions',
              },
              {
                title: 'Learning Pool',
                description: 'Dedicated shallow training pool specifically designed for beginners and young learners with comprehensive safety features',
              },
              {
                title: 'Sports Shop & Eatery',
                description: 'Convenient on-site sports shop for swimming gear and refreshments at our eatery',
              },
              {
                title: 'Family Amenities',
                description: 'Comfortable viewing area for parents and spectators, plus a playground for children',
              },
            ].map((facility, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <h3 className="text-xl font-bold text-slate-900 mb-3">{facility.title}</h3>
                <p className="text-slate-600 leading-relaxed">{facility.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block bg-cyan-50 rounded-2xl p-6 border-2 border-cyan-100">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <MapPin className="w-6 h-6 text-cyan-700" />
                <h3 className="text-xl font-bold text-slate-900">Location</h3>
              </div>
              <p className="text-lg text-slate-700 font-medium">
                Achimota Swimming & Leisure Center
              </p>
              <p className="text-slate-600">
                Inside Achimota Secondary School Campus, Accra
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
